import StaleOverlay from "@/components/StaleOverlay";
import { useEffect, useState } from "react";
import { UserPlus, UserX, Users, Percent, Loader2, Info, UserCheck } from "lucide-react";
import { api, formatInt } from "@/lib/api";
import { MetricLabel, QualityNotes } from "@/lib/metrics";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { resolveRange } from "@/lib/dateRange";
import { FriendSourceLabel, CsvReminderBanner, useFriendStatus, md } from "@/components/FriendSource";
import { useAccuracyFlags, accuracyNotes } from "@/components/DataAccuracy";

const SRC = { line_oa_csv: "CSV 匯入", line_insight: "LINE 官方每日統計（暫估）", manual: "手動輸入", mixed: "混合來源" };
const pctTxt = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

function Delta({ pct, inverse }) {
  if (pct == null) return <span className="text-[10px] text-slate-400 font-mono">vs 前期 —</span>;
  const good = inverse ? pct < 0 : pct > 0;
  return <span className={`text-[10px] font-mono ${pct === 0 ? "text-slate-500" : good ? "text-emerald-600" : "text-rose-600"}`}>vs 前期 {pct > 0 ? "+" : ""}{pct}%</span>;
}

function Stat({ label, hint, value, icon: Icon, delta, testid, flags, notes }) {
  return (
    <div className="h-full flex flex-col rounded-xl border border-slate-200 bg-white px-4 py-3 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <MetricLabel label={label} hintKey={hint} flags={flags} notes={notes} testid={`${testid}-info`} className="text-xs font-medium text-slate-500 whitespace-nowrap" />
        <Icon className="h-3.5 w-3.5 shrink-0 text-teal-600" />
      </div>
      <div className="mt-1 text-2xl font-bold font-mono tabular-nums text-slate-900" data-testid={`${testid}-value`}>{value}</div>
      <div className="mt-auto pt-0.5 h-5 flex items-end whitespace-nowrap overflow-hidden" data-testid={`${testid}-footer`}>{delta}</div>
    </div>
  );
}

function SourceChip({ t, accFlags, testid }) {
  return (
    <HoverCard openDelay={100} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap ${accFlags.length ? "border-amber-200 bg-amber-50 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`} data-testid={`${testid}-source`}>
          來源：{t.source ? SRC[t.source] || t.source : "尚未有資料"}<Info className="h-3.5 w-3.5 shrink-0 text-slate-500" data-testid={`${testid}-source-icon`} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="end" className="w-[320px] bg-white border border-gray-200 text-xs text-slate-700 p-3 shadow-xl space-y-1.5" data-testid={`${testid}-source-card`}>
        <div className="font-semibold text-slate-800">好友資料來源</div>
        <FriendSourceLabel c={t} testid={`${testid}-source-label`} />
        {t.coverage_note && <div className="text-slate-500">{t.coverage_note}</div>}
        <QualityNotes notes={accFlags.map((f) => `${f.message_zh}，${f.fix_zh}`)} testid={`${testid}-accuracy`} />
      </HoverCardContent>
    </HoverCard>
  );
}

// F4 — 有效好友數 / 加好友 / 封鎖 / 淨增好友 / 封鎖率 (CSV > LINE 官方每日統計 > 手動, per property per day)
export default function FriendStatsStrip({ rangeKey = "30d", start, end, since, until, propertyId, showByProperty = false, withEffective = false, testid = "friend-stats" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const accFlags = useAccuracyFlags(propertyId || "all", "friends");
  const status = useFriendStatus(withEffective ? propertyId || "all" : null);
  const r = since && until ? { since, until } : resolveRange(String(rangeKey).replace(/^last_(\d+)d$/, "$1d"), start, end);
  useEffect(() => {
    let off = false;
    setLoading(true);
    api.get("/line/friends/summary", { params: { since: r.since, until: r.until, ...(propertyId && propertyId !== "all" ? { property_id: propertyId } : {}) } })
      .then((x) => { if (!off) setData(x.data); }).catch(() => { if (!off) setData({ error: true }); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [r.since, r.until, propertyId]);

  if (!data) return <div className="text-xs text-slate-500 py-3" data-testid={`${testid}-loading`}><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />載入加好友資料…</div>;
  if (data.error) return null;
  const t = data.total || {};
  const d = t.delta_pct || {};
  const none = !t.has_data;
  const cov = (data.by_property || []).filter((p) => p.has_data);
  const partialDays = cov.length === 1 && cov[0].coverage.days_with_data < cov[0].coverage.total_days ? [cov[0].coverage.days_with_data, cov[0].coverage.total_days] : null;
  const effRows = (status?.rows || []).filter((x) => x.effective_friends != null);
  const effMap = Object.fromEntries((status?.rows || []).map((x) => [x.property_id, x]));
  const effTotal = effRows.reduce((a, x) => a + x.effective_friends, 0);
  const effAsOf = effRows.map((x) => x.as_of).sort()[0];
  const effNotes = !status ? undefined : effRows.length === 0 ? ["還沒有 LINE 官方統計：需要場域設定好 Messaging API，系統每天 10:30 抓前一天的數字"] : effRows.length < status.rows.length ? [`只有 ${effRows.length}/${status.rows.length} 個場域有官方統計`] : undefined;
  const foot = (txt) => <span className="text-[10px] text-slate-400">{txt}</span>;
  return (
    <StaleOverlay loading={loading} testid={`${testid}-stale`}>
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid={`${testid}-header`}>
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">LINE 好友增減</span>
          <span className="font-mono text-xs text-slate-500 whitespace-nowrap" data-testid={`${testid}-range`}>{data.range.since} ~ {data.range.until}</span>
        </div>
        <div className="flex items-center gap-2">
          <SourceChip t={t} accFlags={accFlags} testid={testid} />
          <CsvReminderBanner propertyId={propertyId || "all"} testid={`${testid}-csv-reminder`} short />
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-3 ${withEffective ? "md:grid-cols-5" : "lg:grid-cols-4"}`} data-testid={`${testid}-cards`}>
        {withEffective && (
          <Stat testid={`${testid}-effective`} label="目前有效好友數" hint="effective_friends" notes={effNotes} icon={UserCheck}
            value={effRows.length ? formatInt(effTotal) : "—"} delta={foot(effRows.length ? `LINE 官方統計 · 截至 ${md(effAsOf)}` : "LINE 官方統計 · 尚無資料")} />
        )}
        <Stat notes={accuracyNotes(accFlags)} testid={`${testid}-gained`} label="加好友（次數）" hint="friend_adds"
          flags={{ no_data: none, fix: "到「設定 › LINE 加好友管道」上傳 LINE OA 加好友管道 CSV", is_estimated: (t.insight_days || 0) > 0,
                   coverage_days: partialDays?.[0], total_days: partialDays?.[1] }} icon={UserPlus} value={none ? "—" : formatInt(t.gained)} delta={<Delta pct={d.gained} />} />
        <Stat notes={accuracyNotes(accFlags)} testid={`${testid}-blocked`} label="封鎖" hint="friend_blocked" icon={UserX} value={none ? "—" : formatInt(t.blocked)} delta={<Delta pct={d.blocked} inverse />} />
        <Stat testid={`${testid}-net`} label="淨增好友" hint="friend_net" icon={Users} value={none ? "—" : formatInt(t.net)} delta={<Delta pct={d.net} />} />
        <Stat testid={`${testid}-block-rate`} label="封鎖率" hint="friend_block_rate" icon={Percent} value={pctTxt(t.block_rate)} delta={foot("封鎖 ÷ 加好友")} />
      </div>
      {showByProperty && (
        <details className="rounded-lg border border-slate-200 bg-white" data-testid={`${testid}-by-property`}>
          <summary className="cursor-pointer px-3 py-2 text-xs text-slate-600">各場域明細</summary>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-500 border-b border-slate-100">
              <th className="text-left px-3 py-1.5 font-medium">場域</th>
              {withEffective && <th className="text-right px-2 font-medium">有效好友數</th>}
              <th className="text-right px-2 font-medium">加好友</th><th className="text-right px-2 font-medium">封鎖</th>
              <th className="text-right px-2 font-medium">淨增</th><th className="text-right px-2 font-medium">封鎖率</th><th className="text-left px-3 font-medium">來源 / 涵蓋</th>
            </tr></thead>
            <tbody>
              {data.by_property.map((p) => (
                <tr key={p.property_id} className="border-b border-slate-50" data-testid={`${testid}-row-${p.property_id}`}>
                  <td className="px-3 py-1.5 text-slate-800">{p.property_name}</td>
                  {withEffective && <td className="text-right px-2 font-mono" data-testid={`${testid}-row-eff-${p.property_id}`}>{effMap[p.property_id]?.effective_friends != null ? `${formatInt(effMap[p.property_id].effective_friends)}（${md(effMap[p.property_id].as_of)}）` : "—"}</td>}
                  <td className="text-right px-2 font-mono">{p.has_data ? formatInt(p.gained) : "—"}</td>
                  <td className="text-right px-2 font-mono">{p.has_data ? formatInt(p.blocked) : "—"}</td>
                  <td className="text-right px-2 font-mono">{p.has_data ? formatInt(p.net) : "—"}</td>
                  <td className="text-right px-2 font-mono">{pctTxt(p.block_rate)}</td>
                  <td className="px-3 text-slate-500">{p.source ? (SRC[p.source] || p.source) : "—"}{p.coverage_note ? ` · ${p.coverage_note}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
    </StaleOverlay>
  );
}
